'use client';

import { SigninElementDataType } from '@/components/sections/builder/elements/body/signin/type';
import { ElementProps } from '@/components/sections/builder/types';
import { Button } from '@/components/ui/button';
import { FormItem } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { toast } from '@/components/ui/use-toast';
import { setAuthCookies, signin } from '@/lib/actions/customer/action';
import { cn, isEmpty, removeHTMLTags } from '@/lib/utils';
import { Form, Formik } from 'formik';
import { useSearchParams } from 'next/navigation';
import React from 'react';
import * as Yup from 'yup';
import { Label } from '@/components/ui/label';
import { Eye, EyeClosed } from 'lucide-react';
import Link from 'next/link';
import { Checkbox } from '@/components/ui/checkbox-custom';
import ModalResendVerification from '@/components/sections/customer/store/modal-resend-verify-email';
import useModal from '@/hooks/useModal';
import FieldErrorMessage from '@/components/sections/field-error-message';
import { useBuilderStore } from '@/stores/builder';
import { shallow } from 'zustand/shallow';

const SigninSchema = Yup.object().shape({
  email: Yup.string().email('Invalid Format Email').required('Email is a required'),
  password: Yup.string()
    .required('Password is a required')
    .min(8, 'Password must be at least 8 characters')
    .max(64, 'Password must be at most 64 characters'),
});

export default function ElementContentSignin({ host, element, theme, preview }: ElementProps) {
  const query = useSearchParams();
  const isPreview = query.get('isPreview') || false;
  const token = query.get('token') || '';
  const themeId = query.get('theme_id') || '';
  const checkout_url = query.get('checkout_url');

  const [isSubmitted, setIsSubmitted] = React.useState(false);
  const [showPassword, setShowPassword] = React.useState(false);
  const [rememberMe, setRememberMe] = React.useState(false);
  const [emailNotVerifiedError, setEmailNotVerifiedError] = React.useState(false);

  const content = element.data as SigninElementDataType;
  const rememberedUser = JSON.parse(localStorage.getItem('remember_me') || '{}');

  const modalResendVerification = useModal();

  const builderTheme = useBuilderStore((state) => state.theme, shallow);
  const themeContent = preview ? builderTheme.input : theme?.input;

  const initialValues = {
    email: rememberedUser ? rememberedUser.user : '',
    password: rememberedUser ? rememberedUser.password : '',
  };

  const handleOnSubmit = async (values: typeof initialValues) => {
    if (isPreview || !!token) return;

    try {
      setIsSubmitted(true);
      const result = await signin(host as string, values);

      if (result.success) {
        toast({
          title: 'Success',
          description: result.message || 'Sign in successfully.',
          variant: 'success',
        });

        setAuthCookies(result.data?.token as string, result.data?.refreshToken as string);
        setEmailNotVerifiedError(false);
      } else {
        toast({
          title: 'Error',
          description: result.message || 'Failed to sign in',
          variant: 'destructive',
        });
        setEmailNotVerifiedError(true);
      }
    } catch (error) {
      if (error instanceof Error) {
        toast({
          title: 'Error',
          description: error.message || 'Something went wrong.',
          variant: 'destructive',
        });
      }
      setEmailNotVerifiedError(false);
    } finally {
      setIsSubmitted(false);
    }
  };

  const handleShowPassword = () => {
    setShowPassword((prev) => !prev);
  };

  const headingLabel =
    removeHTMLTags(content.headingLabel || '').trim() !== '' ? content.headingLabel : 'Sign up';

  return (
    <>
      <div
        className='signin_padding bg_scheme_signin flex h-full min-h-[20vh] w-full items-center justify-center px-4'
        style={
          {
            '--border-color': content?.colorScheme?.btnSolidBackgroud,
            '--rounded': `${themeContent?.borderCornerRadius || 8}px`,
          } as React.CSSProperties
        }
      >
        <div className='flex w-full max-w-[720px] flex-col items-center'>
          <div className='flex w-full items-start'>
            <div
              className='mb-6 text-lg md:text-xl lg:text-2xl'
              dangerouslySetInnerHTML={{ __html: headingLabel as string }}
            />
          </div>
          <div className='w-full'>
            <Formik
              initialValues={initialValues}
              validationSchema={SigninSchema}
              onSubmit={(values) => {
                if (rememberMe) {
                  localStorage.setItem('rememberedUser', JSON.stringify(values));
                } else {
                  localStorage.removeItem('rememberedUser');
                }
                handleOnSubmit(values);
              }}
            >
              {({ values, errors, handleSubmit, handleChange, handleBlur, touched }) => (
                <Form onSubmit={handleSubmit} className='flex flex-col'>
                  <FormItem className='mb-6'>
                    <div className='flex w-full flex-col'>
                      <Label htmlFor='email' className='mb-3 font-normal'>
                        Email
                      </Label>
                      <Input
                        id='email'
                        name='email'
                        type='email'
                        className={cn(
                          'rounded-[var(--rounded)] font-normal placeholder:text-sm placeholder:font-light focus:!outline-0 focus:!ring-0 active:outline-0 active:ring-0',
                          !isEmpty(content.colorScheme) &&
                            'focus:!border-[var(--border-color)] active:!border-[var(--border-color)]',
                          errors.email && touched.email && 'border-red-500'
                        )}
                        classNameWrapper='w-full'
                        value={values.email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder='Enter your email'
                        errorMessage={
                          errors.email && touched.email && typeof errors.email === 'string'
                            ? errors.email
                            : undefined
                        }
                        required
                        data-test='signin-email'
                      />
                    </div>
                  </FormItem>
                  <FormItem className='mb-6'>
                    <div className='flex w-full flex-col'>
                      <Label htmlFor='password' className='mb-3 font-normal'>
                        Password
                      </Label>
                      <div className='relative'>
                        <Input
                          id='password'
                          name='password'
                          type={showPassword ? 'text' : 'password'}
                          className={cn(
                            'rounded-[var(--rounded)] font-normal placeholder:text-sm placeholder:font-light focus:!outline-0 focus:!ring-0 active:outline-0 active:ring-0',
                            !isEmpty(content.colorScheme) &&
                              'focus:!border-[var(--border-color)] active:!border-[var(--border-color)]',
                            errors.password &&
                              touched.password &&
                              typeof errors.password === 'string' &&
                              'border-red-500'
                          )}
                          value={values.password}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          placeholder='Enter your password'
                          classNameWrapper='w-full'
                          required
                          data-test='signin-password'
                        />
                        <button
                          type='button'
                          onClick={handleShowPassword}
                          className='absolute right-3 top-1/2 -translate-y-1/2 transform text-gray-500'
                        >
                          {showPassword ? (
                            <EyeClosed className='h-4 w-4' />
                          ) : (
                            <Eye className='h-4 w-4' />
                          )}
                        </button>
                      </div>
                      {errors.password &&
                        touched.password &&
                        typeof errors.password === 'string' && (
                          <FieldErrorMessage className='mt-3 text-xs' error={errors.password} />
                        )}
                    </div>
                    <div className='mt-1 flex'>
                      <Link
                        href={`/account/forgot-password${
                          token ? `?theme_id=${themeId}&token=${token}` : ''
                        }`}
                        className='text-sm font-bold'
                        style={{
                          color: content.colorScheme?.btnSolidBackgroud,
                        }}
                        data-test='signin-forget-password'
                      >
                        Forgot password?
                      </Link>
                    </div>
                  </FormItem>
                  <FormItem className='mb-6'>
                    <div className='flex items-center gap-2'>
                      <Checkbox
                        id='remember'
                        name='remember'
                        defaultChecked={false}
                        className={cn(
                          'h-6 w-6 rounded transition-all duration-300',
                          isEmpty(content.colorScheme) && rememberMe
                            ? 'bg-primary text-white'
                            : 'text-white'
                        )}
                        style={
                          rememberMe
                            ? {
                                backgroundColor: content.colorScheme?.btnSolidBackgroud,
                                borderColor: content.colorScheme?.btnSolidBackgroud,
                              }
                            : {
                                borderColor: content.colorScheme?.btnSolidBackgroud,
                              }
                        }
                        onCheckedChange={(value) => {
                          setRememberMe(value as boolean);
                        }}
                        data-test='signin-remember'
                      />
                      <Label
                        htmlFor='remember'
                        className='text-sm font-normal hover:cursor-pointer'
                      >
                        Remember me
                      </Label>
                    </div>
                  </FormItem>
                  <Button
                    className='theme_button w-full text-sm transition-all duration-300 hover:opacity-80'
                    style={
                      !isEmpty(content.colorScheme)
                        ? {
                            backgroundColor: content.colorScheme?.btnSolidBackgroud,
                            color: content?.colorScheme?.btnSolidlabel,
                          }
                        : {}
                    }
                    type='submit'
                    data-test='signin-submit'
                    disabled={(isPreview as boolean) || isSubmitted || !!token}
                  >
                    <span className='text-sm'>
                      {isSubmitted ? 'Please wait...' : content?.buttonLabel || 'Sign in'}
                    </span>
                  </Button>
                </Form>
              )}
            </Formik>
            <div className='mt-4 flex w-full items-center justify-center text-sm'>
              Don&apos;t have an account?{' '}
              <Link
                href={`/account/signup${
                  token ? `?theme_id=${themeId}&token=${token}` : isPreview ? '?isPreview=true' : ''
                }${
                  checkout_url
                    ? token || isPreview
                      ? `&checkout_url=${encodeURIComponent(checkout_url)}`
                      : `?checkout_url=${encodeURIComponent(checkout_url)}`
                    : ''
                }`}
                className='ml-1 font-bold'
                data-test='signin-sigunup-button'
                style={{ color: content.colorScheme?.btnSolidBackgroud }}
              >
                Sign up here
              </Link>
            </div>
            {emailNotVerifiedError && (
              <div className='mt-2 flex items-center justify-center'>
                <button
                  type='button'
                  onClick={() => {
                    modalResendVerification.open();
                  }}
                  className='text-sm '
                  style={{
                    color: content.colorScheme?.btnSolidBackgroud,
                  }}
                >
                  Didn’t receive a verification email? Resend it
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      <ModalResendVerification
        openModal={modalResendVerification.isShow}
        setOpenModal={modalResendVerification.setIsShow}
        host={host}
      />

      <style jsx>
        {`
          .bg_scheme_signin {
            background-color: ${content.colorScheme?.background || 'white'};
            color: ${content.colorScheme?.text || '#52535B'};
          }
          .signin_padding {
            padding-top: ${content.topPadding || 0}px;
            padding-bottom: ${content.bottomPadding || 0}px;
          }
          @media (max-width: 640px) {
            .signin_padding {
              padding-left: 20px;
              padding-right: 20px;
            }
          }
        `}
      </style>
    </>
  );
}
